import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Hammer, MapTrifold, Sparkle, Kanban, CreditCard,
  LinkedinLogo, Gauge, Lightning, ArrowRight, CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import JsonLd, { orgSchema } from "@/components/JsonLd";

function setMeta(name, content) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function FeatureCard({ icon: Icon, title, body, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-5 hover:border-orange-600/40 transition-colors"
    >
      <div className="w-9 h-9 bg-orange-600/10 border border-orange-600/30 rounded-sm flex items-center justify-center mb-3">
        <Icon size={18} weight="duotone" className="text-orange-500" />
      </div>
      <div className="font-display font-semibold text-zinc-100 text-base mb-1.5">{title}</div>
      <div className="text-sm text-zinc-400 leading-relaxed">{body}</div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    document.title = "ClickForge Lead Agent · Real Midwest business leads + AI outreach";
    setMeta(
      "description",
      "Real OSM business discovery, AI audits, cold-email outreach via Resend, and Stripe proposal checkout — for solo agencies and closers in the US Midwest.",
    );
  }, []);

  // If already signed in, send to dashboard
  if (user) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <JsonLd id="landing-org" schema={orgSchema()} />
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-zinc-950/70 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center forge-pulse">
              <Hammer size={16} weight="bold" className="text-zinc-950" />
            </div>
            <div>
              <div className="font-display font-semibold text-base leading-none">ClickForge</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-orange-500 mt-0.5">Lead Agent</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/pricing" data-testid="landing-pricing-link" className="hidden sm:inline-block">
              <Button variant="ghost" className="text-zinc-300 hover:text-orange-400 hover:bg-zinc-900">
                Pricing
              </Button>
            </Link>
            <Link to="/login" data-testid="landing-signin">
              <Button variant="ghost" className="text-zinc-300 hover:text-zinc-100 hover:bg-zinc-900">
                Sign in
              </Button>
            </Link>
            <Link to="/signup" data-testid="landing-cta-top">
              <Button className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm">
                Start free <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-600/10 border border-orange-600/30 rounded-full mb-6">
              <Lightning size={12} weight="fill" className="text-orange-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-400">
                Real Midwest leads · No credit card
              </span>
            </div>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-zinc-50">
              Find, qualify, and close
              <br />
              <span className="text-orange-500">real Midwest businesses</span>
              <br />
              in minutes.
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 mt-6 max-w-2xl leading-relaxed">
              ClickForge is an AI lead-gen agent built for digital agencies serving small businesses across all 12 Midwest states. Real businesses with real phone numbers, AI-generated audits and outreach, all bundled into a CRM pipeline you can run from your phone.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="landing-cta-hero">
                <Button className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm h-12 px-6 text-sm font-semibold">
                  Start free <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-zinc-700 hover:bg-zinc-900 text-zinc-200 rounded-sm h-12 px-6 text-sm">
                  I have an account
                </Button>
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[11px] font-mono text-zinc-500">
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> No API keys required</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> Real leads in 30 seconds</span>
            </div>
          </div>
        </div>
        {/* Decorative grid */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
          backgroundImage: "linear-gradient(rgba(234,88,12,1) 1px, transparent 1px), linear-gradient(90deg, rgba(234,88,12,1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-500 mb-2">
            What&apos;s inside
          </div>
          <h2 className="font-display font-semibold text-2xl sm:text-3xl text-zinc-50">
            Everything an agency needs. Nothing it doesn&apos;t.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard
            icon={MapTrifold}
            title="Real lead discovery"
            body="Pull real local SMBs across all 12 Midwest states. Auto Repair, Dental, Legal, Real Estate, HVAC, Roofing — real names, phones, addresses. Free, no key required."
            testid="feature-discovery"
          />
          <FeatureCard
            icon={Sparkle}
            title="AI audits & outreach"
            body="Claude Sonnet 4.5 writes a full website audit and a personalized email + LinkedIn DM for every lead. One click, ready to send."
            testid="feature-ai"
          />
          <FeatureCard
            icon={Kanban}
            title="11-stage CRM pipeline"
            body="Drag leads through New → Qualified → Booked Call → Proposal → Won. Bulk actions, audit log, 30-day score history. Built for daily prospecting."
            testid="feature-pipeline"
          />
          <FeatureCard
            icon={LinkedinLogo}
            title="Free LinkedIn helpers"
            body="One-click 'Find owner' and 'Company' buttons open pre-filled LinkedIn searches. Layer in Sales Navigator CSV imports for high-intent B2B contacts."
            testid="feature-linkedin"
          />
          <FeatureCard
            icon={Gauge}
            title="Lead scoring & reviews"
            body="Auto-scores 0-100 on website quality, SEO, reviews, and contact completeness. PageSpeed scans run keyless. Optional Google/Yelp review monitoring."
            testid="feature-scoring"
          />
          <FeatureCard
            icon={CreditCard}
            title="Stripe paid proposals"
            body="Send $199-$2,500 ClickForge package proposals with one click. Real Stripe checkout pages. Auto-advances leads to Won on payment."
            testid="feature-stripe"
          />
        </div>
      </section>

      {/* Workflow */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16 border-t border-zinc-900">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-500 mb-2">How it works</div>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl text-zinc-50 mb-4">
              From cold city to booked call in five minutes.
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6">
              No Apollo subscriptions, no ZoomInfo seats, no spreadsheets. Pick a city, pick an industry, click Find — real businesses appear in your pipeline. Then let Claude write the outreach while you grab coffee.
            </p>
            <Link to="/signup" data-testid="landing-cta-workflow">
              <Button className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm h-11 px-5 text-sm font-semibold">
                Start free <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
          <ol className="space-y-3">
            {[
              { n: "01", t: "Pick a Midwest city", d: "Sioux Falls, Minneapolis, Omaha, Detroit, Chicago — or any US city" },
              { n: "02", t: "Pick an industry", d: "Auto Repair, Legal, Dental, Real Estate, Restaurants, HVAC, Plumbing…" },
              { n: "03", t: "Click Find real businesses", d: "10-50 real SMBs added to your pipeline with phone, website, address" },
              { n: "04", t: "AI audits + outreach in one click", d: "Claude writes the email + LinkedIn DM personalized to each lead" },
              { n: "05", t: "Send, follow up, close", d: "Track every reply through 11 stages → Stripe checkout when ready" },
            ].map((s) => (
              <li key={s.n} className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4 flex gap-4">
                <div className="font-mono text-orange-500 text-xs pt-0.5">{s.n}</div>
                <div className="flex-1">
                  <div className="font-display font-medium text-zinc-100 text-sm">{s.t}</div>
                  <div className="text-xs text-zinc-500 mt-1">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="bg-gradient-to-br from-orange-600/15 via-zinc-900/40 to-zinc-900/40 border border-orange-600/30 rounded-sm p-8 md:p-12 text-center">
          <h2 className="font-display font-semibold text-3xl sm:text-4xl text-zinc-50 mb-3">
            Stop buying lead lists.
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 mb-6 max-w-xl mx-auto">
            Real Midwest businesses, real phone numbers, real AI outreach — all free to start. Sign up and book your first call today.
          </p>
          <Link to="/signup" data-testid="landing-cta-final">
            <Button className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm h-12 px-7 text-sm font-semibold">
              Start free <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
          <div className="mt-3 text-[11px] font-mono text-zinc-500">
            No credit card · No fees · Cancel anytime (there&apos;s nothing to cancel)
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
            <Hammer size={12} weight="bold" className="text-orange-500" />
            ClickForge Digital Solutions · clickforgeds.com
          </div>
          <div className="flex gap-4 text-[11px] font-mono text-zinc-500">
            <Link to="/login" className="hover:text-zinc-300">Sign in</Link>
            <Link to="/signup" className="hover:text-zinc-300">Sign up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
